import styled from "styled-components";

import { Color } from "src/elements";
import type { PlanogramSeam } from "./aisleBreakSeams";
import type { PlanogramAisleBreakEditing, PlanogramRendererLabels } from "./types";

export const SEAM_HOVER_HALF_WIDTH_PX = 8;

export const AISLE_BREAK_HANDLE_ATTRIBUTE = "data-aisle-break-handle";

const HandleRoot = styled.div.attrs<{ $x: number; $y: number }>(({ $x, $y }) => ({
  style: { left: $x, top: $y },
}))`
  position: absolute;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  /* Just above the pointer, never under it: a click at the bay edge still reaches the canvas. */
  transform: translate(-50%, calc(-100% - 12px));
  pointer-events: auto;
`;

const Pill = styled.button`
  padding: 4px 12px;
  border: 1px solid ${Color.primary};
  border-radius: 999px;
  background: ${Color.white};
  color: ${Color.primary};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);

  &:hover,
  &:focus-visible {
    background: ${Color.primary};
    color: ${Color.white};
  }
`;

const Tooltip = styled.div`
  padding: 4px 8px;
  border-radius: 3px;
  background: ${Color.textMain};
  color: ${Color.white};
  font-size: 12px;
  white-space: nowrap;
`;

interface Props {
  editing: PlanogramAisleBreakEditing;
  labels: Pick<PlanogramRendererLabels, "breakTheAisleHere" | "joinBays">;
  seam: PlanogramSeam;
  x: number;
  y: number;
}

export const AisleBreakSeamHandle = ({ editing, labels, seam, x, y }: Props) => {
  const handleProps = { [AISLE_BREAK_HANDLE_ATTRIBUTE]: seam.bayId };

  return (
    <HandleRoot
      $x={x}
      $y={y}
      data-is-break={String(seam.isBreak)}
      data-testid="aisle-break-seam-handle"
      {...handleProps}
    >
      {seam.isBreak && (
        <Tooltip data-testid="aisle-break-tooltip" role="tooltip">
          {editing.describeBreak(seam.bayId)}
        </Tooltip>
      )}
      <Pill
        data-testid="aisle-break-seam-pill"
        onClick={() => editing.onSetSplitAfter(seam.bayId, !seam.isBreak)}
        type="button"
      >
        {seam.isBreak ? labels.joinBays(seam.bayNo, seam.bayNo + 1) : labels.breakTheAisleHere}
      </Pill>
    </HandleRoot>
  );
};
